import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  CircularProgress,
} from '@mui/material';
import { BRAND_COLORS, alpha } from '../../styles/theme';
import { useFlightMetadataOptions } from '../../hooks/useFlightMetadataOptions';
import { AddEntityDialog } from '../common/AddEntityDialog';
import {
  useFetchFlightWeatherMutation,
  useUpdateFlightMetadataMutation,
} from './flightDetails.mutations';
import { useAuthV2 } from '../../hooks/useAuthV2';
import { openAnalyticsWithFiles } from '../file-browser/utils';
import { flightHandoffEntry } from '../../utils/flightFileRefs';
import { Flight, FlightEditState, WeatherData } from './flightDetails.types';
import { FlightDetailRow } from './FlightDetailRow';

interface FlightDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  flightDate: string | null;
  droneSerial: string;
  flights: Flight[];
  loading?: boolean;
  // May be async — callers (Latest Flights, Files tab) re-fetch on update. The
  // call site fires it fire-and-forget; the returned promise is intentionally ignored.
  onFlightUpdated?: () => void | Promise<void>;
}

const labelStyle = {
  color: 'text.secondary',
  textTransform: 'uppercase' as const,
  fontSize: '0.7rem',
};

export const FlightDetailsDialog: React.FC<FlightDetailsDialogProps> = ({
  open,
  onClose,
  flightDate,
  droneSerial,
  flights,
  loading = false,
  onFlightUpdated,
}) => {
  const [editingFlightId, setEditingFlightId] = useState<string | null>(null);
  const [editState, setEditState] = useState<FlightEditState | null>(null);
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [showAddProject, setShowAddProject] = useState(false);
  const [fetchingWeather, setFetchingWeather] = useState<string | null>(null);
  const [localWeather, setLocalWeather] = useState<Record<string, WeatherData | null>>({});

  const fetchFlightWeatherMutation = useFetchFlightWeatherMutation();
  const updateFlightMetadataMutation = useUpdateFlightMetadataMutation(droneSerial, flightDate);
  const { profile } = useAuthV2();

  const {
    flightTypes,
    payloads,
    pilots,
    customers,
    projects,
    airspaceObservers,
    payloadOperators,
    loading: loadingOptions,
    addCustomer,
    addProject,
  } = useFlightMetadataOptions({ enabled: open });

  // Reset edit state when dialog closes
  useEffect(() => {
    if (!open) {
      setEditingFlightId(null);
      setEditState(null);
    }
  }, [open]);

  const handleDownload = (downloadUrl: string, flightType: string) => {
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `flight_${flightType}_${flightDate}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePlot = (flight: Flight) => {
    // An ADDRESS, not a label — and since #39 Phase C the key is neither. This
    // used to pop the key's last segment for a filename and hand the key over
    // as a path; for a minted uuid that names no file, has no `.csv` suffix,
    // and `/api/s3/download-csv` answers "File must be a csv file" (#1294).
    //
    // `flightHandoffEntry` is the same shared helper ProfileTab's bulk "Plot
    // Data" uses, so the two surfaces cannot drift: `object_id` leads, the
    // legacy path arm stays for rows without one, and the filename comes from
    // the row because the viewer routes CSV-vs-BIN off it.
    const orgSlug = profile?.organization?.slug ?? null;
    openAnalyticsWithFiles([flightHandoffEntry(flight, orgSlug)], orgSlug);
  };

  const handleFetchWeather = async (flightId: string) => {
    setFetchingWeather(flightId);
    try {
      const response = await fetchFlightWeatherMutation.mutateAsync(flightId);
      if (response.success && response.weather) {
        setLocalWeather((prev) => ({ ...prev, [flightId]: response.weather }));
      }
    } catch {
      // Already toasted by the global MutationCache handler (lib/queryClient).
      // Without this catch the rejection escapes mutateAsync as an unhandled
      // promise rejection and lands in Sentry (DASHBOARD-FRONTEND-3, #1124) —
      // for what is a normal "this log has no GPS position" answer.
    } finally {
      setFetchingWeather(null);
    }
  };

  const getWeatherForFlight = (flight: Flight): WeatherData | null => {
    // Check local state first (for freshly fetched weather)
    if (localWeather[flight.flight_id]) {
      return localWeather[flight.flight_id];
    }
    // Fall back to flight's weather from server
    return flight.weather;
  };

  const handleStartEdit = (flight: Flight) => {
    setEditingFlightId(flight.flight_id);
    setEditState({
      flight_type: flight.flight_type || '',
      pilot_id: flight.pilot_id || '',
      payload_id: flight.payload_id?.toString() || '',
      customer_id: flight.customer_id || '',
      project_id: flight.project_id || '',
      airspace_observer_id: flight.airspace_observer_id || '',
      payload_operator_id: flight.payload_operator_id || '',
    });
  };

  const handleCancelEdit = () => {
    setEditingFlightId(null);
    setEditState(null);
  };

  const handleSave = async () => {
    if (!editingFlightId || !editState) return;

    // Send empty string to clear fields (backend converts "" to null)
    // Don't convert to null here, or backend thinks field wasn't provided
    await updateFlightMetadataMutation.mutateAsync({
      flightId: editingFlightId,
      data: {
        flight_type: editState.flight_type,
        pilot_id: editState.pilot_id,
        payload_id: editState.payload_id ? parseInt(editState.payload_id) : 0,
        customer_id: editState.customer_id,
        project_id: editState.project_id,
        airspace_observer_id: editState.airspace_observer_id,
        payload_operator_id: editState.payload_operator_id,
      },
    });
    setEditingFlightId(null);
    setEditState(null);
    onFlightUpdated?.();
  };

  const handleAddCustomer = async (name: string) => {
    const newCustomer = await addCustomer(name);
    if (editState) {
      setEditState({ ...editState, customer_id: newCustomer.id });
    }
  };

  const handleAddProject = async (name: string) => {
    const newProject = await addProject(name);
    if (editState) {
      setEditState({ ...editState, project_id: newProject.id });
    }
  };

  // flightTypes arrive pre-ordered by display_order from the backend; no client sort.
  const flightTypeOptions = flightTypes.map((t) => ({ id: t.name, label: t.name }));
  const pilotOptions = pilots.map((p) => ({ id: p.id, label: p.full_name || p.email }));
  const payloadOptions = payloads.map((p) => ({
    id: p.id,
    label: p.name + (p.weight ? ` (${p.weight}kg)` : ''),
  }));
  const customerOptions = customers.map((c) => ({ id: c.id, label: c.name }));
  const projectOptions = projects.map((p) => ({ id: p.id, label: p.name }));
  const airspaceObserverOptions = airspaceObservers.map((o) => ({
    id: o.id,
    label: o.full_name || o.email,
  }));
  const payloadOperatorOptions = payloadOperators.map((o) => ({
    id: o.id,
    label: o.full_name || o.email,
  }));

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ color: BRAND_COLORS.forestGreen.main }}>
          Flight Details - {flightDate}
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : flights.length === 0 ? (
            <Typography variant="body1" color="text.secondary">
              No flight data available for this date.
            </Typography>
          ) : (
            <Box>
              {flights.map((flight, index) => {
                const isThisFlightEditing = editingFlightId === flight.flight_id;

                return (
                  <Box key={flight.flight_id}>
                    {index > 0 && <Divider sx={{ my: 2 }} />}
                    <FlightDetailRow
                      flight={flight}
                      isThisFlightEditing={isThisFlightEditing}
                      editingFlightId={editingFlightId}
                      editState={editState}
                      setEditState={setEditState}
                      loadingOptions={loadingOptions}
                      flightTypeOptions={flightTypeOptions}
                      pilotOptions={pilotOptions}
                      payloadOptions={payloadOptions}
                      customerOptions={customerOptions}
                      projectOptions={projectOptions}
                      airspaceObserverOptions={airspaceObserverOptions}
                      payloadOperatorOptions={payloadOperatorOptions}
                      weather={getWeatherForFlight(flight)}
                      isFetchingWeather={fetchingWeather === flight.flight_id}
                      isSaving={updateFlightMetadataMutation.isPending}
                      labelStyle={labelStyle}
                      onStartEdit={handleStartEdit}
                      onCancelEdit={handleCancelEdit}
                      onSave={handleSave}
                      onFetchWeather={handleFetchWeather}
                      onDownload={handleDownload}
                      onPlot={handlePlot}
                      onAddCustomer={() => setShowAddCustomer(true)}
                      onAddProject={() => setShowAddProject(true)}
                    />
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={onClose}
            variant="contained"
            sx={{
              backgroundColor: BRAND_COLORS.forestGreen.main,
              '&:hover': { backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.8) },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Customer Dialog */}
      <AddEntityDialog
        open={showAddCustomer}
        onClose={() => setShowAddCustomer(false)}
        onAdd={handleAddCustomer}
        title="Add New Customer"
        label="Customer Name"
      />

      {/* Add Project Dialog */}
      <AddEntityDialog
        open={showAddProject}
        onClose={() => setShowAddProject(false)}
        onAdd={handleAddProject}
        title="Add New Project"
        label="Project Name"
      />
    </>
  );
};
